import { useCallback, useRef } from 'react';

import {
  ESTIMATED_CHARACTER_READ_TIME_IN_MS,
  MIN_ANNOUNCEMENT_READ_TIME_IN_MS,
} from './useAnnouncementQueue.constants';
import type { Announcement, EnhancedAnnouncement } from './useLiveRegion.types';

export function useAnnouncementQueue(
  process: (announcement: Announcement) => void
) {
  const queue = useRef<EnhancedAnnouncement[]>([]);
  const idRef = useRef(0);

  const getNextAnnouncement = useCallback(() => {
    const sortedQueue = [...queue.current].sort((a, b) => {
      if (a.priority === 'assertive' && b.priority !== 'assertive') return -1;
      if (a.priority !== 'assertive' && b.priority === 'assertive') return 1;
      return a.timestamp - b.timestamp;
    });

    return sortedQueue[0];
  }, [queue]);

  const removeAnnouncement = useCallback((id: number) => {
    queue.current = queue.current.filter((item) => item.id !== id);
  }, []);

  const processQueue = useCallback(() => {
    if (queue.current.length === 0) {
      return;
    }

    const nextAnnouncement = getNextAnnouncement();
    process(nextAnnouncement);
    setTimeout(() => {
      removeAnnouncement(nextAnnouncement.id);
      // eslint-disable-next-line react-hooks/immutability
      processQueue();
    }, estimateReadingTime(nextAnnouncement.message));
  }, [getNextAnnouncement, process, removeAnnouncement]);

  const addAnnouncement = useCallback(
    (announcement: Announcement) => {
      queue.current = [
        ...queue.current,
        { ...announcement, id: ++idRef.current, timestamp: Date.now() },
      ];

      if (queue.current.length === 1) {
        processQueue();
      }
    },
    [processQueue]
  );

  return { addAnnouncement };
}

function estimateReadingTime(message: string) {
  return Math.max(
    MIN_ANNOUNCEMENT_READ_TIME_IN_MS,
    message.length * ESTIMATED_CHARACTER_READ_TIME_IN_MS
  );
}
